import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { findReferencedProductTypes, ReferenceProductTypesResult } from '~/Api/HrPriceReferencing/MaxPrice';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	createExportXls,
	CreateExportXlsResult,
	ErrorResponse,
	findHrMaxPrice,
	HrMaxPriceResult,
	Update,
	updateHrMaxPrice as apiUpdateHrMaxPrice,
	ValidationResult,
} from '~/Api/User/ProductGroup/HrMaxPrice';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage } from '~/FlashMessage/actions';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export const fetchHrMaxPrice =
	(groupId: string): ThunkAction<Promise<HrMaxPriceResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findHrMaxPrice, userId, groupId));
		dispatch({ type: ActionType.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				groupId,
				hrMaxPrice: response?.hrMaxPrice ?? null,
				request,
				type: ActionType.receive,
			})
		);
	};

export const updateHrMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateHrMaxPrice(groupId, update)));
		dispatch({ type: ActionType.requestUpdate, groupId, request });
		return request;
	};

export const immediatelyUpdateHrMaxPrice =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateHrMaxPrice, userId, groupId, update))
		);
		dispatch({ type: ActionType.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				groupId,
				hrMaxPrice: response?.hrMaxPrice ?? null,
				request,
				type: ActionType.receiveUpdate,
			})
		);
	};

export const handleHrMaxPriceUpdateResponse =
	<Response>(response: Response, groupId: string): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}
		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchHrMaxPrice(groupId)).catch(handleError);
		}
		if (response.handleIllegalPriceTypeSelected()) {
			dispatch(flashErrorMessage('Product reference prices has changed, please check selected product prices'));
			dispatch(fetchHrMaxPrice(groupId)).catch(handleError);
		}
		dispatch(handleError(response));
	};

export const createHrMaxPriceExportXls =
	(groupId: string): ThunkAction<Promise<CreateExportXlsResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(createExportXls, userId, groupId));
		dispatch({ type: ActionType.requestXlsCreate, groupId, request });

		return resolve(request, (response, error) =>
			dispatch({
				request,
				type: ActionType.receiveXlsCreate,
				validationResult:
					error !== null
						? error instanceof ErrorResponse
							? error.toValidationResult()
							: null
						: new ValidationResult(false, [], []),
			})
		);
	};

export function fetchReferenceProductTypes(): ThunkAction<Promise<ReferenceProductTypesResult>, Action> {
	return async (dispatch, getState) => {
		const { productTypes } = getState().siMaxPrice;
		if (productTypes !== null) {
			return productTypes.request;
		}

		const request = dispatch(invokeApi(findReferencedProductTypes));
		dispatch({ type: ActionType.requestProductTypes, request });

		return resolve(request, (response) =>
			dispatch({
				type: ActionType.receiveProductTypes,
				request,
				types: response?.items || null,
			})
		);
	};
}

export const pruneHrMaxPrice = (groupId: string): Action => ({
	type: ActionType.prune,
	groupId,
});

export const scheduleHrMaxPriceUpdate =
	(groupId: string, update: Update): ThunkAction<Promise<void>, Action> =>
	async (dispatch) =>
		new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateHrMaxPrice(groupId, update)));
			}, 2000);

			dispatch({ groupId, update, timeoutId, type: ActionType.scheduleUpdate });
		});

export const commitHrMaxPriceUpdate =
	(groupId: string): ThunkAction<Promise<void>, Action> =>
	async (dispatch, getState) => {
		const { update } = getState().hrMaxPrice;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, data } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateHrMaxPrice(groupId, data));
		}
	};

export const handleCommitError =
	<Error>(error: Error): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		error.handleIllegalPriceTypeSelected();
		dispatch(handleError(error));
	};
